"use client";

import { Button } from "@ui/components/core/Button";
import { logger } from "@utilities/logger";
import { cn } from "@ui/lib/utils";
import { Subtitles } from "@utilities/subtitles/subtitleTypes";
import { cva, type VariantProps } from "class-variance-authority";
import { Volume2Icon, VolumeXIcon } from "lucide-react";
import * as m from "motion/react-m";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

const boxedAspectRatioVideoVariants = cva("relative flex items-center justify-center overflow-hidden rounded-xl", {
  variants: {
    boxAspectRatio: {
      "1x1": "aspect-1x1",
      "4x3": "aspect-4x3",
      "16x9": "aspect-16x9",
      "9x16": "aspect-9x16",
    },
    background: {
      neutral: "bg-neutral",
      none: "",
    },
  },
  defaultVariants: {
    boxAspectRatio: "1x1",
    background: "neutral",
  },
});

export interface BoxedAspectRatioVideoProps extends VariantProps<typeof boxedAspectRatioVideoVariants> {
  subtitles?: Subtitles;
  loop?: boolean;
  autoPlay?: boolean;
  children?: ReactNode;
  className?: string;
  isMuted: boolean;
  onMuteToggle: () => void;
  onVideoClick?: () => void;
  onVideoRef?: (ref: HTMLVideoElement | null) => void;
  preload?: "auto" | "metadata" | "none";
  src: string;
  /** Background style for the video container. Defaults to "neutral". */
  background?: "neutral" | "none";
}

export function BoxedAspectRatioVideo({
  src,
  subtitles,
  isMuted,
  onMuteToggle,
  onVideoRef,
  onVideoClick,
  autoPlay = true,
  loop = true,
  preload = "auto",
  className = "",
  boxAspectRatio = "1x1",
  background = "neutral",
  children,
}: BoxedAspectRatioVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoAspectRatio, setVideoAspectRatio] = useState<number | null>(null);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      const aspectRatio = videoRef.current.videoWidth / videoRef.current.videoHeight;
      setVideoAspectRatio(aspectRatio);

      // Call onVideoRef callback when metadata is loaded
      if (onVideoRef) {
        onVideoRef(videoRef.current);
      }
      if (autoPlay) {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            logger.error(error instanceof Error ? error : null, "Video autoplay failed");
          });
        }
      }
    }
  }, [autoPlay, onVideoRef]);

  // Check if metadata is already loaded (e.g., from cache)
  useEffect(() => {
    const video = videoRef.current;
    // Reset aspect ratio when src changes
    setVideoAspectRatio(null);
    if (video && video.videoWidth > 0 && video.videoHeight > 0) {
      handleLoadedMetadata();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const handleOnEnded = useCallback(() => {
    if (!loop || !videoRef.current) return;
    videoRef.current.currentTime = 0;
    videoRef.current.play();
  }, [loop]);

  // Calculate target aspect ratio value
  const getTargetRatio = useCallback((ratio: string | null | undefined) => {
    if (!ratio) return 1;
    const [w, h] = ratio.split("x").map(Number);
    return w / h;
  }, []);

  const targetRatioValue = getTargetRatio(boxAspectRatio);
  // Check if video aspect ratio matches box aspect ratio (with small tolerance)
  const isMatchingRatio = videoAspectRatio !== null && Math.abs(videoAspectRatio - targetRatioValue) < 0.01;

  return (
    <>
      <div className={cn(boxedAspectRatioVideoVariants({ boxAspectRatio, background }), className)}>
        <m.div
          key={src}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={videoAspectRatio !== null ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className={`relative ${
            videoAspectRatio !== null && videoAspectRatio > targetRatioValue ? "w-full" : "h-full"
          } ${!isMatchingRatio ? "p-12" : ""}`}
          style={{ aspectRatio: videoAspectRatio ?? targetRatioValue }}
        >
          <video
            className='h-full w-full rounded-xl object-cover'
            muted={isMuted}
            playsInline
            preload={preload}
            ref={videoRef}
            src={src}
            onClick={onVideoClick}
            onLoadedMetadata={handleLoadedMetadata}
            style={{ cursor: onVideoClick ? "pointer" : undefined }}
            onEnded={handleOnEnded}
          >
            {subtitles && (
              <track label={subtitles.label} kind='subtitles' srcLang={subtitles.srcLang} src={subtitles.src} default />
            )}
          </video>

          {/* Mute Button */}
          <Button
            className='absolute right-16 top-16 z-10'
            onClick={e => {
              e.stopPropagation();
              onMuteToggle();
            }}
            size='pill'
            variant='elevated'
          >
            {!isMuted ? <Volume2Icon fill='currentColor' size={20} /> : <VolumeXIcon fill='currentColor' size={20} />}
          </Button>

          {/* Children (e.g., progress bar) */}
          {children}
        </m.div>
      </div>
    </>
  );
}
